import { useEffect, useMemo } from 'react';
import { Building2, ChevronsUpDown } from '@/components/icons';
import { ROLE_LABELS } from '@1qa/shared';
import { useAuth } from '@/lib/auth';

/** Native <select> styled as a switcher: accessible and keyboard friendly out of the box. */
export function OrgSwitcher() {
  const { memberships, currentOrgId, setCurrentOrgId, currentOrgRole } = useAuth();

  const options = useMemo(() => memberships.map((m) => ({ id: m.orgId, name: m.orgName })), [memberships]);

  useEffect(() => {
    const first = options[0];
    if (!currentOrgId && first) setCurrentOrgId(first.id);
  }, [currentOrgId, options, setCurrentOrgId]);

  if (options.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-field px-3 py-2.5 text-sm text-fg-muted ring-1 ring-border ring-inset">
        <Building2 className="size-4" /> No organization yet
      </div>
    );
  }

  const roleLabel = currentOrgRole ? ROLE_LABELS[currentOrgRole] : '';

  return (
    <label className="group relative block rounded-field bg-surface ring-1 ring-border ring-inset transition hover:bg-surface-raised focus-within:ring-primary">
      <span className="sr-only">Current organization</span>
      <div className="pointer-events-none flex items-center gap-3 px-3 py-2.5">
        <span className="grid size-8 place-items-center rounded-full bg-primary-subtle text-sm font-semibold text-primary">
          {(options.find((o) => o.id === currentOrgId)?.name ?? '?').charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-fg">
            {options.find((o) => o.id === currentOrgId)?.name ?? 'Select organization'}
          </span>
          {roleLabel && <span className="block text-xs text-fg-muted">{roleLabel}</span>}
        </span>
        <ChevronsUpDown className="size-3 text-fg-secondary" />
      </div>
      <select
        className="absolute inset-0 cursor-pointer opacity-0"
        value={currentOrgId ?? ''}
        onChange={(e) => setCurrentOrgId(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
